/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */

export const VISIBLE_RECENT_SEARCHES = 5;

export interface SearchOverlayOptionLike {
  displayName?: string;
  name?: string;
  text?: string;
  timestamp?: number;
  type?: string;
}

export interface SearchOverlayPanelState<T extends SearchOverlayOptionLike> {
  displayResults: T[];
  hasPanel: boolean;
  hasResults: boolean;
  panelTitle: string;
  resultCounter: string;
  searchLoading: boolean;
  stateText: string;
}

export interface SearchOverlayPanelStateParams<T extends SearchOverlayOptionLike> {
  loading: boolean;
  options: T[];
  recentSearches: T[];
  searchValue: string;
  translate: SearchOverlayTranslate;
  // a typed term shorter than this asks for more characters instead ...
  minSearchLength?: number;
}

export type SearchOverlayTranslate = (
  key: string,
  params?: Record<string, number>
) => string;

/**
 * Resolves the display name for a search option.
 *
 * ```typescript
 * import {getSearchOverlayOptionName} from '@ion/dls/src/components/search/SearchOverlayUtils';
 *
 * ...
 *
 * const label = getSearchOverlayOptionName(option);
 * ```
 *
 * @param option Defines the search option to resolve.
 * @returns The first available display text, otherwise an empty string.
 */
export const getSearchOverlayOptionName = (option: SearchOverlayOptionLike) => (
  option.displayName || option.name || option.text || ''
);

/**
 * Builds a stable React key for a search option row.
 *
 * ```typescript
 * import {getSearchOverlayOptionKey} from '@ion/dls/src/components/search/SearchOverlayUtils';
 *
 * ...
 *
 * const key = getSearchOverlayOptionKey(option, index);
 * ```
 *
 * @param option Defines the search option represented by the row.
 * @param index  Defines the option's list index fallback.
 * @returns A stable key based on type, label, and timestamp when available.
 */
export const getSearchOverlayOptionKey = (option: SearchOverlayOptionLike, index: number) => (
  `${option.type || 'search'}-${getSearchOverlayOptionName(option)}-${option.timestamp || index}`
);

/**
 * Resolves visible overlay options for search results or recent searches.
 *
 * ```typescript
 * import {getVisibleSearchOverlayOptions} from '@ion/dls/src/components/search/SearchOverlayUtils';
 *
 * ...
 *
 * const visibleOptions = getVisibleSearchOverlayOptions(searchText, results, history);
 * ```
 *
 * @param searchValue    Defines the active search text.
 * @param options        Defines all matching search results.
 * @param recentSearches Defines all recent search options.
 * @returns Full result options while searching, otherwise the most recent five.
 */
export const getVisibleSearchOverlayOptions = (
  searchValue: string,
  options: SearchOverlayOptionLike[],
  recentSearches: SearchOverlayOptionLike[]
) => (
  searchValue ? options : recentSearches.slice(0, VISIBLE_RECENT_SEARCHES)
);

/**
 * Resolves visible typed overlay options without losing caller item shape.
 *
 * ```typescript
 * import {getTypedVisibleSearchOverlayOptions} from '@ion/dls/src/components/search/SearchOverlayUtils';
 *
 * ...
 *
 * const visibleOptions = getTypedVisibleSearchOverlayOptions(searchText, results, history);
 * ```
 *
 * @param searchValue    Defines the active search text.
 * @param options        Defines all matching search results.
 * @param recentSearches Defines all recent search options.
 * @returns Full result options while searching, otherwise the most recent five.
 */
export function getTypedVisibleSearchOverlayOptions<T extends SearchOverlayOptionLike>(
  searchValue: string,
  options: T[],
  recentSearches: T[]
) {
  return searchValue ? options : recentSearches.slice(0, VISIBLE_RECENT_SEARCHES);
}

/**
 * Resolves translated panel state for search results, loading, and recent searches.
 *
 * ```typescript
 * import {getSearchOverlayPanelState} from '@ion/dls/src/components/search/SearchOverlayUtils';
 *
 * ...
 *
 * const panelState = getSearchOverlayPanelState({
 *   loading,
 *   options,
 *   recentSearches,
 *   searchValue,
 *   translate
 * });
 * ```
 *
 * @param params Defines results, recent searches, loading state, and translator.
 * @returns Derived panel visibility, labels, counts, and displayed rows.
 */
export function getSearchOverlayPanelState<T extends SearchOverlayOptionLike>({
  loading,
  options,
  recentSearches,
  searchValue,
  translate,
  minSearchLength = 0
}: SearchOverlayPanelStateParams<T>): SearchOverlayPanelState<T> {
  const termLength     = searchValue.trim().length;
  // a typed term too short to search shows no (older) results, only a hint ...
  const searchTooShort = !!termLength && termLength < minSearchLength;
  const searchLoading  = loading && !!searchValue && !searchTooShort;
  const visibleResults = getTypedVisibleSearchOverlayOptions(searchValue, options, recentSearches);
  const displayResults = searchLoading || searchTooShort ? [] : visibleResults;
  let stateText        = translate(`common:search.${loading ? 'loadingTextDefault' : 'noResultsTextDefault'}`);
  if (searchTooShort) {
    stateText = translate('common:search.minSearchLength', {count: minSearchLength});
  }

  return {
    displayResults,
    hasPanel      : !!searchValue || !!recentSearches.length,
    hasResults    : !!displayResults.length,
    panelTitle    : translate(`common:search.${searchValue ? 'searchResults': 'recentlySearched'}`),
    resultCounter : searchTooShort ? '' : translate('common:search.resultsCount', {count: options.length}),
    stateText,
    searchLoading
  };
}
